/**
 * Firebase Authentication — Lazy, On-Demand
 *
 * CIRCADIA-PERF: Firebase Auth is initialized lazily.
 * signInWithCustomToken only runs when a Firebase-dependent feature
 * (chat, AI chat, cloud functions) is first accessed.
 * No auth listeners run at startup — zero overhead until needed.
 */

import type { User as FirebaseUser } from "firebase/auth";
import { getFirebaseAuthInstance } from "./config";
import { APIProvider, authController } from "../AppController";
import { parseJwt } from "../util/jwt";

const FIREBASE_TOKEN_ENDPOINT =
  "/patient-centric-app-v2/patient-app/auth/firebase-token";

let isInitializing = false;
let initializationPromise: Promise<boolean> | null = null;

async function getCognitoIdToken(): Promise<string | null> {
  try {
    const token = await authController.getAuthenticationToken();
    if (!token) {
      console.error(
        "[Firebase Auth] getAuthenticationToken returned empty token"
      );
      return null;
    }
    return token;
  } catch (error) {
    console.error(
      "[Firebase Auth] Failed to get ID token via authController:",
      error
    );
    return null;
  }
}

async function getFirebaseToken(
  cognitoIdToken: string
): Promise<string | null> {
  try {
    const response = await APIProvider.POST(
      FIREBASE_TOKEN_ENDPOINT,
      {},
      { Authorization: `Bearer ${cognitoIdToken}` }
    );
    if (response?.firebaseToken) {
      return response.firebaseToken;
    }
    console.error("[Firebase Auth] Token exchange failed:", response);
    return null;
  } catch (error) {
    console.error("[Firebase Auth] Failed to get Firebase token:", error);
    return null;
  }
}

export async function initializeFirebaseAuth(): Promise<boolean> {
  if (isInitializing && initializationPromise) {
    return initializationPromise;
  }

  const auth = await getFirebaseAuthInstance();

  if (auth.currentUser) {
    try {
      const cognitoToken = await getCognitoIdToken();
      if (cognitoToken) {
        const { sub } = parseJwt(cognitoToken);
        if (sub && sub !== auth.currentUser.uid) {
          console.warn(
            "[Firebase Auth] Stale Firebase session detected — cached UID:",
            auth.currentUser.uid,
            "current Cognito sub:",
            sub
          );
          const { signOut } = await import("firebase/auth");
          await signOut(auth);
        } else {
          return true;
        }
      }
    } catch {
      // Cross-check failed, proceed with full re-auth
    }
  }

  isInitializing = true;

  initializationPromise = (async () => {
    try {
      const cognitoIdToken = await getCognitoIdToken();
      if (!cognitoIdToken) {
        console.warn("[Firebase Auth] No Cognito session available");
        return false;
      }

      const firebaseToken = await getFirebaseToken(cognitoIdToken);
      if (!firebaseToken) {
        console.error(
          "[Firebase Auth] Failed to get Firebase token from backend"
        );
        return false;
      }

      const authInstance = await getFirebaseAuthInstance();
      const { signInWithCustomToken } = await import("firebase/auth");
      await signInWithCustomToken(authInstance, firebaseToken);
      return true;
    } catch (error) {
      console.error("[Firebase Auth] Authentication failed:", error);
      return false;
    } finally {
      isInitializing = false;
      initializationPromise = null;
    }
  })();

  return initializationPromise;
}

export async function signOutFirebase(): Promise<void> {
  try {
    const auth = await getFirebaseAuthInstance();
    const { signOut } = await import("firebase/auth");
    await signOut(auth);
  } catch {
    // Silently fail
  }
}

export function getCurrentFirebaseUser(): FirebaseUser | null {
  // Synchronous — returns null if auth hasn't been lazily initialized yet
  return (globalThis as any).__circadia_fb_auth?.currentUser ?? null;
}

export function getCurrentUserId(): string | null {
  return (globalThis as any).__circadia_fb_auth?.currentUser?.uid ?? null;
}

export function isFirebaseAuthenticated(): boolean {
  return (globalThis as any).__circadia_fb_auth?.currentUser != null;
}

export function onFirebaseAuthStateChanged(
  callback: (user: FirebaseUser | null) => void
): () => void {
  // Lazy: only set up the listener if auth has been initialized
  getFirebaseAuthInstance().then(async (auth) => {
    (globalThis as any).__circadia_fb_auth = auth;
    const { onAuthStateChanged } = await import("firebase/auth");
    onAuthStateChanged(auth, callback);
  });
  return () => {};
}

export async function ensureFirebaseAuth(): Promise<string> {
  const auth = await getFirebaseAuthInstance();
  (globalThis as any).__circadia_fb_auth = auth;

  if (auth.currentUser) {
    return auth.currentUser.uid;
  }

  const success = await initializeFirebaseAuth();
  // auth.currentUser is mutated by signInWithCustomToken inside initializeFirebaseAuth
  const uid = (auth as any).currentUser?.uid as string | undefined;
  if (!success || !uid) {
    throw new Error("Firebase authentication required");
  }

  return uid;
}

// Re-export for backward compatibility (proxy from config.ts)
export { firebaseAuth } from "./config";
